import React from 'react'
import config from '../../../constants/config'
import { getBase64, useTranslation, formatDateTime } from '../../../constants/helpers'

const Mau4 = ({ id, player, season, team, sponsors, isShowBirthDay, onSuccess }) => {
    const { t } = useTranslation()
    const width = 640
    const height = width * 1.41
    const px = width / 100

    if (!player) return null

    const isPlayer = player?.types?.[0] === 'football_player_type'
    const positionLabel = isPlayer
        ? `${t(player?.types?.[0])}`
        : `${t(player?.positions?.[0])}`

    const navy = '#1a2a5e'
    const gold = '#f5a623'
    const white = '#ffffff'
    const lightGray = '#f4f6fa'

    return (
        <div
            id={id}
            style={{
                width,
                height,
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: white,
                fontFamily: 'SFUHelveticaCondensedBold'
            }}
        >
            {/* ===== TOP NAVY AREA ===== */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                height: `${px * 58}px`,
                background: `linear-gradient(150deg, ${navy} 0%, #223474 60%, #1a2a5e 100%)`,
            }}>
                {/* Subtle dot pattern */}
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)`,
                    backgroundSize: `${px * 3}px ${px * 3}px`
                }} />

                {/* Diagonal white wave cut */}
                <div style={{
                    position: 'absolute', bottom: `-${px * 0.5}px`, left: 0, right: 0,
                    height: `${px * 12}px`,
                    background: white,
                    clipPath: 'ellipse(55% 100% at 50% 100%)'
                }} />
            </div>

            {/* ===== HEADER: logos + season ===== */}
            <div style={{
                position: 'absolute', top: `${px * 3}px`, left: `${px * 4}px`, right: `${px * 4}px`,
                display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'
            }}>
                {/* Team logo */}
                <div style={{
                    width: `${px * 11}px`, height: `${px * 11}px`,
                    borderRadius: '50%',
                    border: `${px * 0.4}px solid ${gold}`,
                    backgroundColor: white,
                    overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <ImageStatic src={team?.logo} style={{ width: '85%', height: '85%', objectFit: 'contain' }} />
                </div>

                {/* Season name */}
                <p style={{
                    color: gold, fontSize: `${px * 4}px`, fontWeight: 'bold',
                    textTransform: 'uppercase', textAlign: 'center',
                    margin: 0, flex: 1, padding: `0 ${px * 2}px`,
                    letterSpacing: '0.5px',
                    textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }}>{season?.name}</p>

                {/* League logo */}
                <div style={{
                    width: `${px * 11}px`, height: `${px * 11}px`,
                    borderRadius: '50%',
                    border: `${px * 0.4}px solid ${gold}`,
                    backgroundColor: white,
                    overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <ImageStatic src={season?.logo} style={{ width: '85%', height: '85%', objectFit: 'contain' }} />
                </div>
            </div>

            {/* ===== AVATAR (centered, overlapping split) ===== */}
            <div style={{
                position: 'absolute',
                top: `${px * 17}px`,
                left: '50%',
                transform: 'translateX(-50%)',
                width: `${px * 44}px`, height: `${px * 44}px`,
                zIndex: 10
            }}>
                {/* Gold outer ring */}
                <div style={{
                    position: 'absolute', inset: 0,
                    borderRadius: '50%',
                    border: `${px * 1.2}px solid ${gold}`,
                    boxShadow: `0 4px 16px rgba(245,166,35,0.35), 0 2px 8px rgba(0,0,0,0.3)`
                }} />
                {/* White inner ring */}
                <div style={{
                    position: 'absolute', inset: `${px * 1.2}px`,
                    borderRadius: '50%',
                    border: `${px * 0.6}px solid ${white}`,
                }} />
                {/* Avatar */}
                <Image
                    onChange={onSuccess}
                    src={player?.avatar}
                    style={{
                        position: 'absolute',
                        inset: `${px * 1.8}px`,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        backgroundColor: '#dde3f0'
                    }}
                />
            </div>

            {/* ===== INFO AREA (white section) ===== */}
            <div style={{
                position: 'absolute',
                top: `${px * 62}px`,
                left: 0, right: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: `0 ${px * 5}px`
            }}>
                {/* Player name */}
                <h1 style={{
                    color: navy, fontSize: `${px * 7.5}px`, fontWeight: '900',
                    textTransform: 'uppercase', textAlign: 'center',
                    margin: 0, lineHeight: 1.05,
                    letterSpacing: `${px * 0.05}px`
                }}>{player?.name}</h1>

                {/* Gold accent line */}
                <div style={{
                    width: `${px * 30}px`, height: `${px * 0.5}px`,
                    background: gold, margin: `${px * 1.2}px 0`
                }} />

                {/* Position + shirt number row */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: `${px * 2}px` }}>
                    <span style={{
                        color: navy, fontSize: `${px * 3.2}px`,
                        textTransform: 'uppercase', fontStyle: 'italic'
                    }}>{positionLabel}</span>

                    {isPlayer && player?.shirtNumber != null && (
                        <div style={{
                            background: navy,
                            borderRadius: `${px * 1}px`,
                            padding: `${px * 0.3}px ${px * 1.5}px`,
                            display: 'flex', alignItems: 'center'
                        }}>
                            <span style={{ color: gold, fontSize: `${px * 3}px`, fontWeight: 'bold' }}>{player?.shirtNumber}</span>
                        </div>
                    )}
                </div>

                {/* Birthday */}
                {isShowBirthDay && (
                    <div style={{
                        marginTop: `${px * 1.5}px`,
                        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: `${px}px`
                    }}>
                        <span style={{ color: '#888', fontSize: `${px * 2.4}px` }}>📅</span>
                        <span style={{ color: '#555', fontSize: `${px * 2.8}px`, textTransform: 'uppercase' }}>
                            {t('birthday')}: <strong style={{ color: navy }}>{formatDateTime(player?.birthday, 'date')}</strong>
                        </span>
                    </div>
                )}

                {/* Team name row */}
                <div style={{
                    marginTop: `${px * 1.5}px`,
                    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: `${px}px`
                }}>
                    <div style={{
                        width: `${px * 5}px`, height: `${px * 5}px`,
                        borderRadius: '50%', overflow: 'hidden',
                        border: `1px solid ${gold}`
                    }}>
                        <ImageStatic src={team?.logo} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                    <p style={{
                        color: navy, fontSize: `${px * 3}px`,
                        fontWeight: 'bold', textTransform: 'uppercase', margin: 0
                    }}>{team?.name}</p>
                </div>
            </div>

            {/* ===== SPONSOR BAR ===== */}
            {sponsors?.length > 0 && (
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: `${px * 9}px`,
                    backgroundColor: lightGray,
                    borderTop: `${px * 0.3}px solid rgba(0,0,0,0.08)`,
                    display: 'flex', flexDirection: 'row', alignItems: 'center',
                    justifyContent: 'center', gap: `${px * 2}px`,
                    padding: `0 ${px * 3}px`
                }}>
                    {sponsors?.slice(0, 8)?.map((item, idx) => (
                        <ImageStatic key={idx} src={item?.logo} style={{
                            height: `${px * 5.5}px`, width: 'auto',
                            objectFit: 'contain',
                            backgroundColor: white,
                            padding: `${px * 0.3}px ${px * 0.8}px`,
                            borderRadius: `${px * 0.4}px`
                        }} />
                    ))}
                </div>
            )}
        </div>
    )
}

// Image with getBase64 + onChange trigger
const Image = props => {
    const [data, setData] = React.useState(null)
    React.useEffect(() => {
        if (data) setData(null)
        if (props?.src?.includes('ui-avatars')) {
            getBase64(config.logo).then(r => setData(r))
        } else {
            getBase64(props.src).then(r => setData(r))
        }
    }, [props.src, props.id])
    React.useEffect(() => {
        if (data) props?.onChange?.()
    }, [data])
    return data ? <img {...props} src={data} alt="" /> : null
}

// Static image (logos – không trigger export)
const ImageStatic = props => {
    const [data, setData] = React.useState(null)
    React.useEffect(() => {
        if (props?.src?.includes('ui-avatars')) {
            getBase64(config.logo).then(r => setData(r))
        } else {
            getBase64(props.src).then(r => setData(r))
        }
    }, [props.src])
    return data ? <img {...props} src={data} alt="" /> : null
}

function areEqual(p, n) {
    return (
        JSON.stringify(p.player) === JSON.stringify(n.player) &&
        JSON.stringify(p.team) === JSON.stringify(n.team) &&
        JSON.stringify(p.season) === JSON.stringify(n.season) &&
        JSON.stringify(p.sponsors) === JSON.stringify(n.sponsors)
    )
}

export default React.memo(Mau4, areEqual)
